import { cancel, call, fork, put, take } from 'redux-saga/effects';
import { isEqual } from 'lodash';
import { resetApp } from 'containers/App/session/actions';

/**
 * Generator function to run an API request & handle errors thrown
 * If the error is 403 (unauthorized), then the resetApp() action is dispatched
 * @param  {function} request - the function to call to execute API request
 * @param  {any[]}    requestArgs - parameters to pass to the request call
 * @return {object} an object with either the "response" or "error" field set, depending on what occured
 */
function* withApiErrorHandling(request, ...requestArgs) {
    try {
        const response = yield call(request, ...requestArgs);
        return { response };
    } catch (error) {
        if (error?.output?.statusCode === 403) {
            yield put(resetApp());
        }
        return { error };
    }
}

/**
 * Acts like takeLatest but only triggers if the action is different (i.e.
 * different parameters) or nothing is running.
 * If a new request is made, the previous one is cancelled.
 * @param  {string}     actionType
 * @param  {Function}   saga - saga to run
 * @param  {Array<any>} args - additional args to call the saga with
 * @return {Generator}         [description]
 */
const takeNewest = (actionType, saga, ...args) =>
    fork(function* takeNewestInternal() {
        let prevTask = null;
        let prevAction = null;
        while (true) {
            const action = yield take(actionType);
            const taskIsRunning = prevTask !== null && prevTask.isRunning();
            const sameAction = isEqual(prevAction, action);

            if (!(taskIsRunning && sameAction)) {
                if (taskIsRunning) yield cancel(prevTask);
                prevAction = action;
                prevTask = yield fork(saga, ...args.concat(action));
            }
        }
    });

export { withApiErrorHandling, takeNewest };
